import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Mail, CheckCircle2, Send } from "lucide-react";
import { publicApi } from "../lib/api";
import BackToLifeGuide from "../components/BackToLifeGuide";

/**
 * Public "Contact Us" page.
 *
 * Sends the visitor's message to the LifeGuide inbox via Resend
 * (POST /api/contact). The backend sets Reply-To to the visitor's email
 * so hitting Reply lands directly in their inbox.
 */
export default function ContactPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const canSubmit =
    !busy &&
    name.trim().length > 0 &&
    /^\S+@\S+\.\S+$/.test(email.trim()) &&
    message.trim().length >= 5;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await publicApi.contact({
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim() || null,
        message: message.trim(),
      });
      setSent(true);
      toast.success("Message sent — we'll be in touch.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Could not send your message. Please try again in a moment.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAF7] text-[#2E2E2E]">
      <header className="border-b border-[#DCE5DB]/70 bg-white/70 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2.5"
            data-testid="contact-home-link"
          >
            <div className="w-9 h-9 rounded-2xl bg-[#4F6F52] flex items-center justify-center shadow-[0_4px_14px_rgba(79,111,82,0.25)]">
              <Sparkles size={16} className="text-[#F9FAF7]" strokeWidth={1.8} />
            </div>
            <span className="font-display text-xl text-[#4F6F52]">LifeGuide</span>
          </Link>
          <BackToLifeGuide />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12 sm:py-16">
        <div className="mb-10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#4F6F52]/10 mb-5">
            <Mail size={22} className="text-[#4F6F52]" strokeWidth={1.8} />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight text-[#2E2E2E] mb-3">
            Say hello.
          </h1>
          <p className="text-[#6B6B6B] max-w-md mx-auto leading-relaxed">
            Questions, feedback, prayer requests, or something you'd like to
            see in LifeGuide? Send us a note and we'll read every word.
          </p>
        </div>

        {sent ? (
          <div
            className="bg-white border border-[#DCE5DB] rounded-3xl p-10 text-center shadow-[0_4px_14px_rgba(0,0,0,0.03)]"
            data-testid="contact-sent-confirmation"
          >
            <CheckCircle2
              size={38}
              className="mx-auto mb-4 text-[#4F6F52]"
              strokeWidth={1.6}
            />
            <h2 className="font-display text-2xl mb-2">Message received</h2>
            <p className="text-[#6B6B6B] leading-relaxed mb-6">
              Thanks for reaching out. We'll reply to{" "}
              <span className="text-[#2E2E2E] font-medium">{email}</span> as
              soon as we can — usually within a day or two.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#4F6F52] text-[#F9FAF7] font-medium hover:bg-[#3F5A42] transition"
              data-testid="contact-back-home-btn"
            >
              Back to LifeGuide
            </Link>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="bg-white border border-[#DCE5DB] rounded-3xl p-7 sm:p-10 shadow-[0_4px_14px_rgba(0,0,0,0.03)] space-y-5"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label
                  htmlFor="contact-name"
                  className="block text-sm font-medium text-[#2E2E2E] mb-1.5"
                >
                  Your name
                </label>
                <input
                  id="contact-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                  required
                  autoComplete="name"
                  className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5DB] bg-[#F9FAF7] focus:outline-none focus:border-[#4F6F52] focus:bg-white text-[#2E2E2E]"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label
                  htmlFor="contact-email"
                  className="block text-sm font-medium text-[#2E2E2E] mb-1.5"
                >
                  Email
                </label>
                <input
                  id="contact-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5DB] bg-[#F9FAF7] focus:outline-none focus:border-[#4F6F52] focus:bg-white text-[#2E2E2E]"
                  data-testid="contact-email-input"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="contact-subject"
                className="block text-sm font-medium text-[#2E2E2E] mb-1.5"
              >
                Subject{" "}
                <span className="text-[#6B6B6B] font-normal">(optional)</span>
              </label>
              <input
                id="contact-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                maxLength={200}
                className="w-full px-4 py-2.5 rounded-xl border border-[#DCE5DB] bg-[#F9FAF7] focus:outline-none focus:border-[#4F6F52] focus:bg-white text-[#2E2E2E]"
                data-testid="contact-subject-input"
              />
            </div>

            <div>
              <label
                htmlFor="contact-message"
                className="block text-sm font-medium text-[#2E2E2E] mb-1.5"
              >
                Message
              </label>
              <textarea
                id="contact-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                rows={7}
                maxLength={5000}
                placeholder="Tell us what's on your mind…"
                className="w-full px-4 py-3 rounded-xl border border-[#DCE5DB] bg-[#F9FAF7] focus:outline-none focus:border-[#4F6F52] focus:bg-white text-[#2E2E2E] resize-y leading-relaxed"
                data-testid="contact-message-input"
              />
              <div className="mt-1 text-xs text-[#6B6B6B] text-right">
                {message.length} / 5000
              </div>
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#4F6F52] text-[#F9FAF7] font-medium hover:bg-[#3F5A42] transition disabled:bg-[#B7C4B4] disabled:cursor-not-allowed"
              data-testid="contact-submit-btn"
            >
              {busy ? (
                "Sending…"
              ) : (
                <>
                  Send message <Send size={15} strokeWidth={2} />
                </>
              )}
            </button>

            <p className="text-xs text-[#6B6B6B] text-center leading-relaxed">
              We only use your email to reply to your message. No newsletter,
              no auto-responses.
            </p>
          </form>
        )}
      </main>
    </div>
  );
}
